/**
 * Bandeja de áudios da chamada.
 *
 * Abre por cima da barra de controles, como um teclado de efeitos: um toque no
 * botão e o som sai para todo mundo na sala. O painel fica ancorado ao botão
 * que o abriu em vez de virar um modal centralizado porque tocar um som é um
 * gesto rápido e repetido, e cobrir a chamada inteira para isso atrapalha quem
 * está assistindo alguém transmitir.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Pencil, Play, Plus, Trash2, Volume2 } from 'lucide-react'
import { LIMITS } from '@nexus/shared'
import { ContextMenu, MenuItem, Slider, useCamadaDeSobreposicao } from '@/components/ui'
import { cn } from '@/lib/cn'
import { prepararSom } from '@/lib/bandeja-de-sons'
import { usePreferencesStore } from '@/store/preferences'
import { useSoundsStore } from '@/store/sounds'

/** Extensões que o servidor aceita, para o seletor de arquivos já filtrar. */
const ACEITOS = 'audio/mpeg,audio/ogg,audio/wav,audio/x-wav,audio/mp4,.mp3,.ogg,.wav,.m4a'

/**
 * Largura do painel, encolhida quando a tela é menor que ela.
 *
 * Fixa em trezentos e vinte, num celular de trezentos e vinte o painel ficava
 * exatamente do tamanho da tela e a margem de oito pixels o empurrava para
 * fora, cortando a coluna da esquerda.
 */
function larguraDoPainel(): number {
  return Math.min(320, window.innerWidth - 16)
}

export function SoundboardTray({
  /** Botão que abriu, usado para ancorar o painel. */
  ancora,
  onClose,
}: {
  ancora: HTMLElement | null
  onClose: () => void
}) {
  const camada = useCamadaDeSobreposicao()
  const painelRef = useRef<HTMLDivElement>(null)
  const arquivoRef = useRef<HTMLInputElement>(null)

  const sounds = useSoundsStore((s) => s.sounds)
  const carregado = useSoundsStore((s) => s.carregado)
  const carregando = useSoundsStore((s) => s.carregando)
  const enviando = useSoundsStore((s) => s.enviando)
  const erro = useSoundsStore((s) => s.erro)
  const tocandoAgora = useSoundsStore((s) => s.tocandoAgora)
  const carregar = useSoundsStore((s) => s.carregar)
  const enviar = useSoundsStore((s) => s.enviar)
  const renomear = useSoundsStore((s) => s.renomear)
  const remover = useSoundsStore((s) => s.remover)
  const tocar = useSoundsStore((s) => s.tocar)
  const limparErro = useSoundsStore((s) => s.limparErro)

  const volume = usePreferencesStore((s) => s.audio.volumeDaBandeja)
  const setAudio = usePreferencesStore((s) => s.setAudio)

  const [menu, setMenu] = useState<{ soundId: string; x: number; y: number } | null>(null)
  const [renomeando, setRenomeando] = useState<string | null>(null)
  const [rascunho, setRascunho] = useState('')

  useEffect(() => {
    void carregar()
  }, [carregar])

  /* Fecha ao clicar fora ou com Esc, como todo painel flutuante daqui. */
  useEffect(() => {
    const noDocumento = (e: MouseEvent) => {
      const alvo = e.target as HTMLElement
      if (painelRef.current?.contains(alvo)) return
      if (ancora?.contains(alvo)) return
      // Um menu de contexto aberto vive fora do painel; fechar tudo junto
      // cancelaria o clique em "Renomear" antes dele chegar.
      if (alvo.closest?.('[role="menu"]')) return
      onClose()
    }
    const noTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', noDocumento)
    document.addEventListener('keydown', noTeclado)
    return () => {
      document.removeEventListener('mousedown', noDocumento)
      document.removeEventListener('keydown', noTeclado)
    }
  }, [ancora, onClose])

  const largura = larguraDoPainel()
  const caixa = ancora?.getBoundingClientRect()
  const esquerda = caixa
    ? Math.min(
        Math.max(8, caixa.left + caixa.width / 2 - largura / 2),
        Math.max(8, window.innerWidth - largura - 8),
      )
    : 8

  /*
    Abre para cima quando cabe, para baixo quando não cabe.

    O botão vive no rodapé em quase todo lugar, e ali abrir para cima é o certo.
    Só que o mesmo painel também é chamado de pontos mais altos da interface, e
    ancorado sempre por baixo ele saía pelo topo da janela, cortando a primeira
    fileira de sons.
  */
  const alturaMaxima = Math.min(window.innerHeight * 0.6, 416)
  const cabeAcima = !caixa || caixa.top - 10 - alturaMaxima >= 8
  const posicaoVertical = caixa
    ? cabeAcima
      ? { bottom: window.innerHeight - caixa.top + 10 }
      : { top: Math.min(caixa.bottom + 10, Math.max(8, window.innerHeight - alturaMaxima - 8)) }
    : { bottom: 80 }

  const escolherArquivo = () => {
    limparErro()
    arquivoRef.current?.click()
  }

  const receberArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (file.size > LIMITS.soundSizeBytes) {
      useSoundsStore.setState({
        erro: `O limite é ${Math.round(LIMITS.soundSizeBytes / 1024)} KB. Corte um trecho menor do áudio.`,
      })
      return
    }

    const nome = file.name.replace(/\.[^.]+$/, '').slice(0, LIMITS.soundName.max) || 'Som'
    await enviar(file, nome)
  }

  /*
    No celular não existe clique com o botão direito. O toque demorado é o gesto
    equivalente, e sem ele renomear e apagar simplesmente não existiriam ali.
  */
  const toqueLongo = useRef<number | null>(null)

  const iniciarToque = (soundId: string, e: React.TouchEvent) => {
    const toque = e.touches[0]
    if (!toque) return
    const { clientX, clientY } = toque
    toqueLongo.current = window.setTimeout(() => {
      toqueLongo.current = null
      setMenu({ soundId, x: clientX, y: clientY })
    }, 500)
  }

  const cancelarToque = () => {
    if (toqueLongo.current !== null) {
      window.clearTimeout(toqueLongo.current)
      toqueLongo.current = null
    }
  }

  useEffect(() => cancelarToque, [])

  const confirmarNome = (soundId: string) => {
    const nome = rascunho.trim().slice(0, LIMITS.soundName.max)
    const atual = sounds.find((s) => s.id === soundId)
    setRenomeando(null)
    if (!nome || !atual || nome === atual.name) return
    void renomear(soundId, nome)
  }

  return createPortal(
    <>
      <div
        ref={painelRef}
        role="dialog"
        aria-label="Efeitos sonoros"
        style={{ left: esquerda, width: largura, ...posicaoVertical }}
        className="fixed z-50 flex max-h-[min(60vh,26rem)] flex-col overflow-hidden rounded-xl border border-line bg-surface-overlay shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
          <div>
            <p className="text-sm font-semibold text-content-primary">Efeitos sonoros</p>
            <p className="text-[11px] text-content-muted">
              {sounds.length} de {LIMITS.soundsPerUser}
            </p>
          </div>

          <button
            type="button"
            onClick={escolherArquivo}
            disabled={enviando || sounds.length >= LIMITS.soundsPerUser}
            className="flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-hover disabled:opacity-40"
          >
            {enviando ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            {enviando ? 'Enviando' : 'Adicionar'}
          </button>
        </div>

        <input
          ref={arquivoRef}
          type="file"
          accept={ACEITOS}
          onChange={(e) => void receberArquivo(e)}
          className="hidden"
        />

        {erro ? (
          <p className="border-b border-line bg-danger-subtle px-3 py-2 text-xs leading-relaxed text-danger">
            {erro}
          </p>
        ) : null}

        <div className="scroll-region flex-1 overflow-y-auto p-2">
          {!carregado && carregando ? (
            <div className="flex items-center justify-center py-8 text-content-muted">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : sounds.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <Volume2 className="mx-auto size-8 text-content-muted" />
              <p className="mt-3 text-sm font-medium text-content-primary">Bandeja vazia</p>
              <p className="mt-1 text-xs leading-relaxed text-content-secondary">
                Envie um MP3 curto e ele fica guardado na sua conta, pronto para tocar em qualquer
                chamada.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {sounds.map((som) =>
                renomeando === som.id ? (
                  <input
                    key={som.id}
                    autoFocus
                    value={rascunho}
                    maxLength={LIMITS.soundName.max}
                    onChange={(e) => setRascunho(e.target.value)}
                    onBlur={() => confirmarNome(som.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmarNome(som.id)
                      if (e.key === 'Escape') setRenomeando(null)
                    }}
                    className="h-14 w-full rounded-lg border border-brand bg-surface-raised px-2 text-center text-xs text-content-primary outline-none"
                  />
                ) : (
                  <button
                    key={som.id}
                    type="button"
                    onClick={() => void tocar(som.id)}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      setMenu({ soundId: som.id, x: e.clientX, y: e.clientY })
                    }}
                    onPointerEnter={() => prepararSom(som.url)}
                    onFocus={() => prepararSom(som.url)}
                    onTouchStart={(e) => iniciarToque(som.id, e)}
                    onTouchEnd={cancelarToque}
                    onTouchMove={cancelarToque}
                    onTouchCancel={cancelarToque}
                    title={som.name}
                    className={cn(
                      'flex h-14 w-full flex-col items-center justify-center gap-1 rounded-lg px-2 transition-all',
                      'bg-surface-raised text-content-secondary hover:bg-surface-hover hover:text-content-primary',
                      // O clarão curto é a única confirmação de que o som saiu:
                      // quem apertou só ouve quando o evento volta do servidor.
                      tocandoAgora === som.id && 'scale-95 bg-brand text-white',
                    )}
                  >
                    <Play className="size-4 shrink-0" />
                    <span className="line-clamp-2 text-[11px] leading-tight">{som.name}</span>
                  </button>
                ),
              )}
            </div>
          )}
        </div>

        <div className="border-t border-line px-3 pb-1">
          <Slider
            value={volume}
            onChange={(v) => setAudio({ volumeDaBandeja: v })}
            min={0}
            max={100}
            step={5}
            label="Volume dos efeitos"
            formatValue={(v) => (v === 0 ? 'mudo' : `${v}%`)}
          />
        </div>
      </div>

      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <MenuItem
            icon={<Pencil />}
            onClick={() => {
              const alvo = sounds.find((s) => s.id === menu.soundId)
              setRascunho(alvo?.name ?? '')
              setRenomeando(menu.soundId)
            }}
          >
            Renomear
          </MenuItem>
          <MenuItem icon={<Trash2 />} danger onClick={() => void remover(menu.soundId)}>
            Apagar
          </MenuItem>
        </ContextMenu>
      ) : null}
    </>,
    camada,
  )
}
